/**
 * @fileoverview Project Breakdown Component
 * @description Bar-style breakdown showing test-case counts per project
 */

import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { FolderKanban } from 'lucide-react';

const PROJECT_COLORS = [
  '#0097A9', '#01426A', '#6366F1', '#7C3AED', '#059669',
  '#D97706', '#DC2626', '#0EA5E9', '#EC4899', '#14B8A6',
];

const ProjectBreakdown = ({ projects }) => {
  if (!projects || projects.length === 0) return null;

  const maxCount = Math.max(...projects.map((p) => p.count));

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid #E5E7EB',
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <FolderKanban size={20} color="#01426A" />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#01426A' }}>
          By Project
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {projects.map((proj, idx) => {
          const barWidth = maxCount > 0 ? (proj.count / maxCount) * 100 : 0;
          const color = PROJECT_COLORS[idx % PROJECT_COLORS.length];

          return (
            <Box key={proj.project} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  minWidth: 180,
                  fontWeight: 500,
                  color: '#374151',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={proj.project}
              >
                {proj.project}
              </Typography>

              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    height: 24,
                    width: `${Math.max(barWidth, 2)}%`,
                    backgroundColor: color,
                    borderRadius: 1,
                    transition: 'width 0.4s ease',
                    minWidth: 4,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#1F2937', minWidth: 30 }}
                >
                  {proj.count}
                </Typography>
              </Box>

              {proj.states && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {Object.entries(proj.states).map(([state, cnt]) => (
                    <Chip
                      key={state}
                      label={`${state}: ${cnt}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default ProjectBreakdown;
